import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export const useProperties = () => {
    const { user, userProfile } = useAuth();
    const [properties, setProperties] = useState([]);
    const [activeProperty, setActivePropertyState] = useState(null);
    const [loading, setLoading] = useState(true);

    // localStorage keys scoped to user
    const getStorageKey = (uid) => `homeezi_active_property_${uid}`;
    const getCacheKey = (uid) => `homeezi_cached_properties_${uid}`;

    const fetchProperties = useCallback(async () => {
        if (!user) return;

        try {
            // Fetch properties the user owns
            const { data: ownedProperties, error: ownedError } = await supabase
                .from("properties")
                .select("*")
                .eq("user_id", user.id)
                .order("created_at", { ascending: true });

            if (ownedError) {
                console.error("Failed to fetch owned properties:", ownedError);
                // Fall back to cached properties if network error
                const cached = localStorage.getItem(getCacheKey(user.id));
                if (cached) {
                    try {
                        const parsed = JSON.parse(cached);
                        if (Array.isArray(parsed) && parsed.length > 0) {
                            setProperties(parsed);
                            const savedId = localStorage.getItem(getStorageKey(user.id));
                            setActivePropertyState(parsed.find(p => p.id === savedId) || parsed[0]);
                        }
                    } catch (e) {
                        console.warn("Failed to parse cached properties:", e);
                    }
                }
                setLoading(false);
                return;
            }

            // Fetch properties the user is a member of (shared with them)
            const { data: sharedProperties, error: sharedError } = await supabase
                .from("property_members")
                .select("property_id, properties(*)")
                .eq("user_id", user.id);

            if (sharedError) {
                console.warn("Could not fetch shared properties (non-fatal):", sharedError.message);
            }

            // Combine owned and shared properties
            const allProperties = [
                ...(ownedProperties || []),
                ...(!sharedError && sharedProperties ? sharedProperties.map(pm => pm.properties).filter(Boolean) : [])
            ];

            // Remove duplicates by ID
            const uniqueProperties = Array.from(
                new Map(allProperties.map(p => [p.id, p])).values()
            );

            // Cache properties for offline support
            localStorage.setItem(getCacheKey(user.id), JSON.stringify(uniqueProperties));

            setProperties(uniqueProperties);

            const savedId = localStorage.getItem(getStorageKey(user.id));
            let active = null;

            if (savedId) {
                active = uniqueProperties.find(p => p.id === savedId) || null;
            }

            if (!active && userProfile?.active_property_id) {
                active = uniqueProperties.find(p => p.id === userProfile.active_property_id) || null;
                if (active) {
                    localStorage.setItem(getStorageKey(user.id), active.id);
                }
            }

            if (!active) {
                active = uniqueProperties[0] || null;
                if (active) {
                    localStorage.setItem(getStorageKey(user.id), active.id);
                }
            }

            setActivePropertyState(active);
        } catch (err) {
            console.error("Error in fetchProperties:", err);
            const cached = localStorage.getItem(getCacheKey(user.id));
            if (cached) {
                try {
                    const parsed = JSON.parse(cached);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        setProperties(parsed);
                        const savedId = localStorage.getItem(getStorageKey(user.id));
                        setActivePropertyState(parsed.find(p => p.id === savedId) || parsed[0]);
                    }
                } catch (e) {}
            }
        } finally {
            setLoading(false);
        }
    }, [user, userProfile]);

    const addProperty = async (property) => {
        if (!user) return null;

        // If this is the first property, make it active
        const isFirst = properties.length === 0;

        const { data, error } = await supabase
            .from("properties")
            .insert({
                user_id: user.id,
                name: property.name,
                address: null,
                suburb: property.suburb || null,
                is_active: isFirst,
            })
            .select()
            .single();

        if (error) {
            toast({
                title: "Error",
                description: "Failed to add property",
                variant: "destructive",
            });
            return null;
        }

        toast({
            title: "Property Added",
            description: `${property.name} has been added.`,
        });

        await fetchProperties();
        return data;
    };

    const setActiveProperty = async (propertyId) => {
        if (!user) return false;

        // Save to localStorage for instant local response
        localStorage.setItem(getStorageKey(user.id), propertyId);

        // Update local state immediately (no need to re-fetch)
        const selected = properties.find(p => p.id === propertyId) || null;
        setActivePropertyState(selected);

        // Update profile in background (persist across devices)
        supabase
            .from("profiles")
            .update({ active_property_id: propertyId })
            .eq("id", user.id)
            .then(({ error }) => {
                if (error) console.error("Failed to sync active property to DB:", error);
            });

        toast({
            title: "Property Switched",
            description: "Active property has been changed.",
        });

        return true;
    };

    const updateProperty = async (
        propertyId,
        updates
    ) => {
        if (!user) return false;

        const { error } = await supabase
            .from("properties")
            .update(updates)
            .eq("id", propertyId);

        if (error) {
            toast({
                title: "Error",
                description: "Failed to update property",
                variant: "destructive",
            });
            return false;
        }

        await fetchProperties();
        return true;
    };

    const deleteProperty = async (propertyId) => {
        if (!user) return false;

        const { error } = await supabase
            .from("properties")
            .delete()
            .eq("id", propertyId);

        if (error) {
            toast({
                title: "Error",
                description: "Failed to delete property",
                variant: "destructive",
            });
            return false;
        }

        toast({
            title: "Property Deleted",
            description: "Property has been removed.",
        });

        await fetchProperties();
        return true;
    };


    useEffect(() => {
        let active = true;
        const timer = setTimeout(() => {
            if (active) {
                fetchProperties();
            }
        }, 0);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [fetchProperties]);

    return {
        properties,
        activeProperty,
        // Add selectedProperty alias for compatibility if needed
        selectedProperty: activeProperty,
        loading,
        addProperty,
        setActiveProperty,
        updateProperty,
        deleteProperty,
        refetch: fetchProperties,
    };
};
